import React from 'react';

export interface UseReorderableOptions<T> {
  /** When true, drag-and-drop and keyboard reorder are disabled */
  disabled?: boolean;
  /**
   * Prefix of the row element's data-testid (e.g. 'features-row-').
   * Used by the global keyboard handler to find the focused row and extract item id.
   */
  rowDataTestIdPrefix?: string;
  /** Horizontal offset for the drag image relative to the row (cursor position) */
  dragImageOffsetX?: number;
  /** Vertical offset for the drag image relative to the row (cursor position) */
  dragImageOffsetY?: number;
  /**
   * Max width (px) of the drag ghost. The row is cloned and constrained to this width
   * so the ghost doesn't show the full table width. Omit or 0 to use the row as-is.
   */
  dragGhostMaxWidth?: number;
  /** Called after reorder with the new list (e.g. to persist) */
  onPersist?: (items: T[], movedItemId?: string) => void;
  /** Return true to skip handling keyboard (e.g. when a dropdown is open) */
  skipKeyboardWhen?: () => boolean;
}

/** Use "__after_last__" as dropTargetRowId to drop after the last item */
export const AFTER_LAST_DROP_TARGET = '__after_last__';

export interface UseReorderableReturn<T> {
  draggedId: string | null;
  dragOverId: string | null;
  /** Index where item will be dropped (for blue line indicator). Null when not dragging over a valid zone. */
  dropPosition: { index: number } | null;
  handleDragStart: (e: React.DragEvent, rowId: string) => void;
  handleDragOver: (
    e: React.DragEvent,
    rowId: string,
    currentItems: T[]
  ) => void;
  handleDragLeave: (e: React.DragEvent, rowId: string) => void;
  handleDrop: (
    e: React.DragEvent,
    dropTargetRowId: string,
    currentItems: T[]
  ) => void;
  handleDragEnd: () => void;
  handleRowKeyDown: (
    e: React.KeyboardEvent,
    rowId: string,
    currentItems: T[]
  ) => void;
}

/**
 * Reusable hook for drag-and-drop and keyboard reordering of lists.
 * Uses the HTML5 Drag and Drop API and Shift+Arrow Up/Down for keyboard.
 */
export function useReorderable<T>(
  items: T[],
  setItems: (items: T[]) => void,
  getItemId: (item: T) => string,
  options: UseReorderableOptions<T> = {}
): UseReorderableReturn<T> {
  const {
    disabled = false,
    rowDataTestIdPrefix = '',
    dragImageOffsetX = 24,
    dragImageOffsetY = 25,
    dragGhostMaxWidth = 0,
    onPersist,
    skipKeyboardWhen
  } = options;

  const [draggedId, setDraggedId] = React.useState<string | null>(null);
  const draggedIdRef = React.useRef<string | null>(null);
  const [dragOverId, setDragOverId] = React.useState<string | null>(null);
  const [dropPosition, setDropPosition] = React.useState<{
    index: number;
  } | null>(null);
  const dropPositionRef = React.useRef<{ index: number } | null>(null);
  dropPositionRef.current = dropPosition;

  const handleDragStart = React.useCallback(
    (e: React.DragEvent, rowId: string) => {
      const target = e.target as HTMLElement;
      const blockedByInteractive = target.closest(
        'button, input, select, textarea, a[href], [role="button"], [role="menuitem"], [contenteditable="true"]'
      );
      if (blockedByInteractive) {
        e.preventDefault();
        return;
      }
      draggedIdRef.current = rowId;
      e.dataTransfer.setData('text/plain', rowId);
      e.dataTransfer.effectAllowed = 'move';
      const rowEl = target.closest(
        `[data-testid="${rowDataTestIdPrefix}${rowId}"]`
      ) as HTMLElement | null;
      if (rowEl) {
        try {
          let dragImageEl: HTMLElement = rowEl;
          if (dragGhostMaxWidth > 0) {
            const ghost = rowEl.cloneNode(true) as HTMLElement;
            ghost.setAttribute('aria-hidden', 'true');
            ghost.style.position = 'absolute';
            ghost.style.top = '-9999px';
            ghost.style.left = '0';
            ghost.style.width = `${dragGhostMaxWidth}px`;
            ghost.style.minWidth = '0';
            ghost.style.maxWidth = `${dragGhostMaxWidth}px`;
            ghost.style.pointerEvents = 'none';
            ghost.style.opacity = '0.95';
            ghost.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            ghost.style.borderRadius = '4px';
            ghost.style.overflow = 'hidden';
            document.body.appendChild(ghost);
            dragImageEl = ghost;
            setTimeout(() => {
              try {
                ghost.remove();
              } catch {
                // ignore
              }
            }, 0);
          }
          e.dataTransfer.setDragImage(
            dragImageEl,
            dragImageOffsetX,
            dragImageOffsetY
          );
        } catch {
          // ignore if setDragImage fails (e.g. in some browsers)
        }
      }

      // Defer UI state so the drag source DOM is not mutated during dragstart
      // (replacing children synchronously cancels HTML5 drag in React).
      requestAnimationFrame(() => {
        if (draggedIdRef.current === rowId) {
          setDraggedId(rowId);
        }
      });
    },
    [rowDataTestIdPrefix, dragImageOffsetX, dragImageOffsetY, dragGhostMaxWidth]
  );

  const handleDragOver = React.useCallback(
    (e: React.DragEvent, rowId: string, currentItems: T[]) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverId(rowId);

      if (rowId === AFTER_LAST_DROP_TARGET) {
        setDropPosition({ index: currentItems.length });
        return;
      }

      const rowIndex = currentItems.findIndex(
        item => getItemId(item) === rowId
      );
      if (rowIndex === -1) return;

      const target = e.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const isBottomHalf = e.clientY >= rect.top + rect.height / 2;
      const dropIndex = isBottomHalf ? rowIndex + 1 : rowIndex;
      setDropPosition({ index: dropIndex });
    },
    [getItemId]
  );

  const handleDragLeave = React.useCallback(
    (e: React.DragEvent, rowId: string) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragOverId(prev => (prev === rowId ? null : prev));
        setDropPosition(null);
      }
    },
    []
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent, dropTargetRowId: string, currentItems: T[]) => {
      e.preventDefault();
      const sourceId = draggedIdRef.current;
      draggedIdRef.current = null;
      setDraggedId(null);
      setDragOverId(null);
      setDropPosition(null);
      if (!sourceId) {
        return;
      }

      const fromIndex = currentItems.findIndex(
        item => getItemId(item) === sourceId
      );
      if (fromIndex === -1) return;

      let toIndex: number;
      if (dropTargetRowId === AFTER_LAST_DROP_TARGET) {
        toIndex = currentItems.length;
      } else {
        const latest = dropPositionRef.current;
        toIndex =
          latest?.index ??
          currentItems.findIndex(item => getItemId(item) === dropTargetRowId);
      }
      if (toIndex === -1) toIndex = currentItems.length;
      if (fromIndex === toIndex || fromIndex === toIndex - 1) {
        return;
      }

      const next = [...currentItems];
      const [removed] = next.splice(fromIndex, 1);
      // toIndex is the insertion slot in the *original* list (item still present).
      // After removing from fromIndex, everything from fromIndex onward shifted left by 1.
      const insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex;
      next.splice(insertAt, 0, removed);

      setItems(next);
      onPersist?.(next, sourceId);
    },
    [setItems, getItemId, onPersist]
  );

  const handleDragEnd = React.useCallback(() => {
    draggedIdRef.current = null;
    setDraggedId(null);
    setDragOverId(null);
    setDropPosition(null);
  }, []);

  const performMove = React.useCallback(
    (rowId: string, direction: 'up' | 'down', currentItems: T[]) => {
      const fromIndex = currentItems.findIndex(
        item => getItemId(item) === rowId
      );
      if (fromIndex === -1) return;

      let toIndex: number;
      if (direction === 'up') {
        if (fromIndex <= 0) return;
        toIndex = fromIndex - 1;
      } else {
        if (fromIndex >= currentItems.length - 1) return;
        toIndex = fromIndex + 1;
      }

      const next = [...currentItems];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);

      setItems(next);
      onPersist?.(next, rowId);
    },
    [setItems, getItemId, onPersist]
  );

  const handleRowKeyDown = React.useCallback(
    (e: React.KeyboardEvent, rowId: string, currentItems: T[]) => {
      if (disabled) return;
      const isShiftUp = e.shiftKey && e.key === 'ArrowUp';
      const isShiftDown = e.shiftKey && e.key === 'ArrowDown';
      if (!isShiftUp && !isShiftDown) return;

      e.preventDefault();
      e.stopPropagation();
      performMove(rowId, isShiftUp ? 'up' : 'down', currentItems);
    },
    [disabled, performMove]
  );

  // Global keyboard handler so Shift+Arrow Up/Down works when focus is anywhere inside a row
  React.useEffect(() => {
    if (disabled || !rowDataTestIdPrefix) return;

    const handleDocKeyDown = (e: KeyboardEvent) => {
      const isShiftUp = e.shiftKey && e.key === 'ArrowUp';
      const isShiftDown = e.shiftKey && e.key === 'ArrowDown';
      if (!isShiftUp && !isShiftDown) return;

      if (skipKeyboardWhen?.()) return;

      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      const rowEl = target.closest(`[data-testid^="${rowDataTestIdPrefix}"]`);
      if (!rowEl) return;

      const dataTestId = (rowEl as HTMLElement).getAttribute('data-testid');
      const rowId = dataTestId?.replace(rowDataTestIdPrefix, '') ?? '';
      if (!rowId) return;

      e.preventDefault();
      e.stopPropagation();

      performMove(rowId, isShiftUp ? 'up' : 'down', items);
    };

    document.addEventListener('keydown', handleDocKeyDown, true);
    return () =>
      document.removeEventListener('keydown', handleDocKeyDown, true);
  }, [disabled, rowDataTestIdPrefix, skipKeyboardWhen, items, performMove]);

  return {
    draggedId,
    dragOverId,
    dropPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleRowKeyDown
  };
}
